import { useEffect, useState } from "react";
import { FileText, Lock, Cpu, Trash2, ShieldCheck } from "lucide-react";

const stages = [
  { from: FileText, to: Lock, label: "Encrypted on upload" },
  { from: Lock, to: Cpu, label: "Analyzed in-memory" },
  { from: Cpu, to: Trash2, label: "Auto-deleted in 24h" },
  { from: Trash2, to: ShieldCheck, label: "Zero data retained" },
];

export function TrustSignalFlow() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => (i + 1) % stages.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="border-y border-border bg-surface/40 py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" />
            Privacy-first processing
          </div>
          <h3 className="mt-3 text-xl font-semibold text-foreground sm:text-2xl">
            How your document moves through legaldecoder
          </h3>
        </div>

        <div className="relative flex items-center justify-between gap-2 sm:gap-4">
          {/* Connector track */}
          <div
            className="pointer-events-none absolute left-[10%] right-[10%] top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-border"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute left-[10%] top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-700 ease-out"
            style={{ width: `${((active + 1) / stages.length) * 80}%` }}
            aria-hidden
          />

          {stages.map((stage, i) => {
            const isActive = i === active;
            const isPast = i < active;
            const Icon = isActive ? stage.to : stage.from;
            return (
              <div key={i} className="relative z-10 flex flex-1 flex-col items-center gap-2">
                <div
                  className={
                    "flex h-14 w-14 items-center justify-center rounded-2xl border-2 bg-white shadow-card transition-all duration-500 ease-out sm:h-16 sm:w-16 " +
                    (isActive
                      ? "scale-110 border-emerald-500 shadow-elegant"
                      : isPast
                        ? "border-emerald-300"
                        : "border-border")
                  }
                >
                  <Icon
                    key={`${i}-${isActive}`}
                    className={
                      "h-6 w-6 transition-colors duration-500 sm:h-7 sm:w-7 " +
                      (isActive
                        ? "text-emerald-600 animate-fade-in"
                        : isPast
                          ? "text-emerald-500"
                          : "text-muted-foreground")
                    }
                    strokeWidth={2}
                  />
                </div>
                <span
                  className={
                    "text-center text-[10px] font-medium leading-tight transition-colors duration-500 sm:text-xs " +
                    (isActive ? "text-foreground" : "text-muted-foreground")
                  }
                >
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>

        <p className="mx-auto mt-8 max-w-xl text-center text-sm leading-relaxed text-muted-foreground">
          Your document never touches a database. Analyzed in-memory.
          <span className="font-medium text-foreground"> Wiped within 24 hours.</span>
        </p>
      </div>
    </section>
  );
}
